import { FormMode, isUpdateWithSummary } from './formUtils';

/**
 * Screens the back-office editor can ask its preview to show (see `Form.js`, message listener).
 * - `form`: the form itself. Closed, full, invited-only and update-refused screens are this same
 *   form with a message as only field, as registration-service-v2 serves them
 * - `confirmation`: what follows the submission
 * - `update`: what a participant sees when coming back to their registration
 */
export type PreviewState = 'form' | 'confirmation' | 'update';

type FormModeValue = typeof FormMode[keyof typeof FormMode];

export type PreviewMode = { mode: FormModeValue; isUpdate: boolean | undefined };

/**
 * The mode and update flag a preview state shows. Without a state, the preview goes back to the
 * form as it loaded. A participant coming back sees what the update mode of the options gives:
 * a summary, or the form itself.
 */
export function getPreviewMode(previewState: PreviewState | undefined, initial: PreviewMode, options?: Record<string, unknown>): PreviewMode {
  switch (previewState) {
    case 'form':
      return { mode: FormMode.FORM, isUpdate: initial.isUpdate };
    case 'confirmation':
      return { mode: FormMode.CONFIRMATION, isUpdate: initial.isUpdate };
    case 'update':
      return { mode: isUpdateWithSummary({ options, isUpdate: true }) ? FormMode.UPDATE_SUMMARY : FormMode.FORM, isUpdate: true };
    default:
      return initial;
  }
}

/**
 * Options of the form being edited, merged over the loaded ones. The steps always come from the
 * message: the service leaves them out of its message screens, so must the preview.
 */
export function mergePreviewOptions(current: Record<string, unknown> | undefined, previewOptions: Record<string, unknown>): Record<string, unknown> {
  return { ...(current || {}), ...previewOptions, steps: previewOptions.steps };
}
